import { CSSProperties } from 'react';
import styled from 'styled-components';
import type { RepresentationData } from '@ardoq/data-model';
import { ComponentRepresentation } from '@ardoq/basic-components';
import { IconClickable } from 'atomicComponents/IconClickable';
import {
  ChevronIconName,
  IconName,
  NEGATIVE_COLOR_CSS_VARIABLE,
} from '@ardoq/icons';
import { colors, s4 } from '@ardoq/design-tokens';
import { classes } from '@ardoq/common-helpers';
import { OtherLabelBadge as OtherLabel } from '@ardoq/graph';
import type { OtherLabel as OtherLabelType } from '@ardoq/graph';
import { ComponentItemTopRow, OtherLabelsContainer } from './atoms';
import { truncationPopoverAttrs } from './truncationPopover';

// Center the label within the row's minimum-height band; once the row grows
// taller than its minimum (body content overflow or a user resize) top-align
// instead, so the count badge / icon / label stay anchored at the top and
// remain visible rather than drifting to the vertical center of a tall row
// (ARD-32762). Same convention as `ComponentMatrixListValueHeaderCellContainer`
// in atoms.ts.
const IconAndLabelContainer = styled.div<{
  $isDark: boolean;
  $isMinHeight?: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: ${({ $isMinHeight }) =>
    $isMinHeight === false ? 'flex-start' : 'center'};
  flex: 1;
  min-width: 0;
  color: ${({ $isDark }) =>
    $isDark ? colors.textOnInvertedStrong : colors.textDefault};
`;

const ComponentIconContainer = styled.span<{
  $iconSize: number;
  $isDark: boolean;
}>`
  width: ${({ $iconSize }) => $iconSize}px;
  height: ${({ $iconSize }) => $iconSize}px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: ${({ $iconSize }) => $iconSize}px;
  color: ${({ $isDark }) =>
    $isDark ? colors.iconOnInvertedStrong : colors.iconStrong};
  ${NEGATIVE_COLOR_CSS_VARIABLE}: ${({ $isDark }) =>
    $isDark ? colors.transparent0 : colors.white950};
`;

export const CountBadge = styled.span<{ $isDark: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 ${s4};
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
  background: ${({ $isDark }) =>
    $isDark ? colors.bgSubtle : colors.bgInvertedSubtle};
  color: ${({ $isDark }) =>
    $isDark ? colors.textModerate : colors.textOnInverted};
`;

const LabelText = styled.span<{ $wrapLabel?: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  ${({ $wrapLabel }) =>
    $wrapLabel
      ? `
          white-space: normal;
          overflow-wrap: break-word;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
        `
      : `
          white-space: nowrap;
          text-overflow: ellipsis;
          display: block;
        `}
`;

type ComponentHeaderCardContentArgs = {
  /** Pre-computed in the view model via `labelsInterface.getComponentDisplayName`
   * — same source the body cells use for their main label. */
  componentLabel: string;
  /** Resolved upstream in the view model — `augmentWithGroupsByDepth` /
   * `toViewpointRowModels` populate it for every `REFERENCED_COMPONENT`
   * header. Stories thread the same augmentation pipeline. */
  representationData: RepresentationData;
  /** Additional field value badges, populated from `viewSettings.additionalHeaderFieldNamesX/Y`
   * via `augmentWithGroupsByDepth` (ARD-32771). Empty array renders no badges. */
  otherLabels: OtherLabelType[];
  /** Pre-computed wrapped text lines per badge from the view-model's
   * `wrapString` pass. When provided, each badge renders its pre-wrapped lines
   * via `OtherLabel`'s `wrappedLines` prop instead of relying on CSS
   * line-breaking, so the rendered height always matches the measurement. */
  otherLabelWrappedLines?: string[][];
  childCount: number;
  showExpander: boolean;
  isHighlighted: boolean;
  isContext: boolean;
  isDark: boolean;
  iconSize: number;
  expanderIconName: IconName | ChevronIconName;
  style?: CSSProperties;
  wrapLabel?: boolean;
  /** True (or undefined) when the row is at its minimum height — center the
   * label; false when the row is taller than its minimum — top-align so the
   * label stays visible (ARD-32762). */
  isMinHeight?: boolean;
  expanderClicked: VoidFunction;
  labelDoubleClicked: VoidFunction;
};

const ComponentHeaderCardContent = ({
  componentLabel,
  representationData,
  otherLabels,
  otherLabelWrappedLines,
  childCount,
  showExpander,
  isHighlighted,
  isContext,
  isDark,
  iconSize,
  expanderIconName,
  style,
  wrapLabel,
  isMinHeight,
  expanderClicked,
  labelDoubleClicked,
}: ComponentHeaderCardContentArgs) => {
  const expanderColor = isDark
    ? colors.textOnInvertedStrong
    : colors.textDefault;

  return (
    <IconAndLabelContainer
      $isDark={isDark}
      $isMinHeight={isMinHeight}
      className={classes(isHighlighted && 'active', isContext && 'context')}
      style={style}
    >
      <ComponentItemTopRow>
        {showExpander && (
          <IconClickable
            display="inline"
            color={expanderColor}
            hoverColor={expanderColor}
            containerStyle={{ fontSize: iconSize }}
            onClick={e => {
              e.stopPropagation();
              expanderClicked();
            }}
            iconName={expanderIconName}
          />
        )}
        <ComponentIconContainer $iconSize={iconSize} $isDark={isDark}>
          <ComponentRepresentation
            isImage={representationData.isImage}
            value={representationData.value}
            icon={representationData.icon}
          />
        </ComponentIconContainer>
        <LabelText
          $wrapLabel={wrapLabel}
          onDoubleClick={labelDoubleClicked}
          {...truncationPopoverAttrs}
        >
          {componentLabel}
        </LabelText>
        {childCount > 0 && (
          <CountBadge $isDark={isDark}>{childCount}</CountBadge>
        )}
      </ComponentItemTopRow>
      {otherLabels.length > 0 && (
        <OtherLabelsContainer>
          {otherLabels.map(([title, caption, showTitle], index) => (
            <OtherLabel
              key={`${title}:${caption}`}
              title={title}
              caption={caption}
              showTitle={showTitle}
              preset="unshadowed"
              wrappedLines={otherLabelWrappedLines?.[index]}
            />
          ))}
        </OtherLabelsContainer>
      )}
    </IconAndLabelContainer>
  );
};

export { ComponentHeaderCardContent };
